/**
 * Аутентификация в веб-клиенте: хранение токенов, обновление access-токена
 * по refresh при 401, вызовы /auth/* (docs/api/openapi.yaml).
 */
import type { AvatarDecoration, Nameplate, NameStyle, PrimaryGuild, ProfileEffect, User } from '@/types/api'
import { ApiError, BASE, api, type VersionInfo } from './api'
import { plural } from './guildUi'

export interface CurrentUser {
  id: string
  username: string
  global_name: string | null
  discriminator: string
  avatar: string | null
  banner: string | null
  bot: boolean
  phone: string
  /** Подтверждённая почта; null — не привязана. */
  email: string | null
  /** Почта подтверждена (имя поля — как у Discord). */
  verified: boolean
  /** Адрес, на который ушло письмо со ссылкой, но по ней ещё не перешли. */
  email_pending: string | null
  /** Заявка на удаление учётной записи; null — заявки нет. */
  deletion_requested_at?: string | null
  mfa_enabled: boolean
  locale: string
  bio: string
  premium_type: number
  created_at: string
  /** Есть 18 лет: доступ к каналам 18+. */
  nsfw_allowed?: boolean
  /** Биты аккаунта: 1 — сотрудник платформы (панель модерации). */
  flags?: number
  /** Порядок серверов в колонке слева, как его расставил сам человек. */
  guild_order?: string[]
  /** Украшения — как их видят другие; выбранное по «Плюсу» после его конца — GET /users/@me/decorations. */
  avatar_decoration_data?: AvatarDecoration | null
  display_name_styles?: NameStyle | null
  profile_effect?: ProfileEffect | null
  primary_guild?: PrimaryGuild | null
  collectibles?: { nameplate: Nameplate | null } | null
}

/** Себя как любого пользователя: для списков, чата и голоса, где нужен объект User. */
export function publicUser(u: CurrentUser): User {
  return {
    id: u.id,
    username: u.username,
    global_name: u.global_name,
    avatar: u.avatar,
    avatar_decoration_data: u.avatar_decoration_data ?? null,
    display_name_styles: u.display_name_styles ?? null,
    primary_guild: u.primary_guild ?? null,
    collectibles: u.collectibles ?? null,
  }
}

export interface TokenPair {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: 'Bearer'
  user: CurrentUser
}

export interface Session {
  id: string
  device: string
  ip: string
  created_at: string
  last_seen_at: string
  current: boolean
}

const KEY_ACCESS = 'klichat.access'
const KEY_REFRESH = 'klichat.refresh'
const KEY_EXPIRES = 'klichat.access_expires'

function read(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    /* приватный режим: сессия живёт до перезагрузки страницы */
  }
}

export const tokens = {
  access: (): string | null => read(KEY_ACCESS),
  refresh: (): string | null => read(KEY_REFRESH),
  save(pair: TokenPair): void {
    write(KEY_ACCESS, pair.access_token)
    write(KEY_REFRESH, pair.refresh_token)
    write(KEY_EXPIRES, String(Date.now() + pair.expires_in * 1000))
  },
  clear(): void {
    write(KEY_ACCESS, null)
    write(KEY_REFRESH, null)
    write(KEY_EXPIRES, null)
  },
  /** Access-токен истекает в ближайшие 30 секунд или уже истёк. */
  accessExpiring(): boolean {
    const exp = Number(read(KEY_EXPIRES) ?? 0)
    return !exp || exp - Date.now() < 30_000
  },
}

/**
 * Действующий access-токен для шлюза: обновляется заранее, чтобы IDENTIFY и RESUME не получали 4004.
 * force — обновить, даже если по нашим часам токен ещё годен: шлюз его уже отверг.
 */
export async function currentAccessToken(force = false): Promise<string> {
  const access = tokens.access()
  if (!force && access && !tokens.accessExpiring()) return access
  return (await refreshSession()).access_token
}

let refreshing: Promise<TokenPair> | null = null

async function rotateSession(): Promise<TokenPair> {
  // Читать после получения блокировки: соседняя вкладка могла уже заменить токен.
  const refresh = tokens.refresh()
  if (!refresh) throw new ApiError(401, 40001, 'Нет refresh-токена')
  try {
    const pair = await api<TokenPair>('/auth/refresh', { method: 'POST', body: JSON.stringify({ refresh_token: refresh }) })
    tokens.save(pair)
    return pair
  } catch (err: unknown) {
    if (err instanceof ApiError && err.status === 401) tokens.clear()
    throw err
  }
}

/** Один запрос внутри вкладки; Web Locks сериализует ротацию между вкладками одного сайта. */
export function refreshSession(): Promise<TokenPair> {
  if (!refreshing) {
    const request = typeof navigator !== 'undefined' && navigator.locks
      ? Promise.resolve(navigator.locks.request('klichat.refresh', rotateSession))
      : rotateSession()
    refreshing = request.finally(() => { refreshing = null })
  }
  return refreshing
}

/** Запрос с access-токеном; при 401 один раз обновляет токен и повторяет. */
export async function authed<T>(path: string, init: RequestInit = {}): Promise<T> {
  const access = tokens.access()
  if (!access) throw new ApiError(401, 40001, 'Не выполнен вход')
  try {
    return await api<T>(path, { ...init, token: `Bearer ${access}` })
  } catch (err) {
    if (!(err instanceof ApiError) || err.status !== 401) throw err
    const pair = await refreshSession()
    return api<T>(path, { ...init, token: `Bearer ${pair.access_token}` })
  }
}

/** Запрос к API за файлом, а не за JSON: тот же токен, тот же повтор после 401. */
export async function authedBlob(path: string): Promise<Blob> {
  const get = async (access: string): Promise<Response> =>
    fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${access}` } })
  const access = tokens.access()
  if (!access) throw new ApiError(401, 40001, 'Не выполнен вход')
  let res = await get(access)
  if (res.status === 401) {
    const pair = await refreshSession()
    res = await get(pair.access_token)
  }
  if (!res.ok) throw new ApiError(res.status, 0, `${res.status}: ${res.statusText}`)
  return res.blob()
}

/** Ответ /auth/methods. */
export interface AuthMethods {
  phone: boolean
  email: boolean
  /** RU — российский адрес; пусто — не смогли отнести к России. */
  country: string
}

export interface PhoneStart {
  verification_id: string
  expires_in: number
  phone: string
  /** Регистрация по почте: адрес маской вместо номера. */
  email?: string
  channel: string
  /**
   * Только для мобильной авторизации: pending — оператор подтверждает номер сам, ждём;
   * needs_code — бесшовно не вышло, спрашиваем код; ok — уже подтверждён. Пусто — обычное SMS.
   */
  status?: VerifyStatus
  /** Сколько цифр в коде: у наших SMS шесть, у мобильной авторизации четыре. */
  code_length?: number
}

/** Заявка на удаление: когда подана и когда сработает. */
export interface DeletionStatus {
  deletion_requested_at: string | null
  delete_at: string | null
}

/** Выгрузка данных: пока не заказывали — none. */
export interface DataExport {
  status: 'none' | 'pending' | 'ready' | 'failed'
  requested_at?: string
  finished_at?: string
  expires_at?: string
  size_bytes?: number
  url?: string
}

export type VerifyStatus = 'pending' | 'needs_code' | 'ok' | 'failed'

/** Ответ на опрос состояния: чем идёт подтверждение (mobileid — решает оператор, sms — наш код). */
export interface VerifyProgress {
  status: VerifyStatus
  code_length?: number
  channel?: string
}

export const authApi = {
  startRegistration: (phone: string, captchaToken?: string) =>
    api<PhoneStart>('/auth/register/phone', {
      method: 'POST',
      body: JSON.stringify(captchaToken ? { phone, captcha_token: captchaToken } : { phone }),
    }),
  completeRegistration: (input: {
    verification_id: string
    code: string
    /** Код рекламной ссылки, по которой пришли: регистрация засчитается каналу. */
    campaign?: string
    username: string
    display_name?: string | undefined
    password: string
    date_of_birth: string
    consent: boolean
    referral_code?: string
    /** Необязательно: письмо со ссылкой для подтверждения уйдёт после регистрации. */
    email?: string
  }) => api<TokenPair>('/auth/register/verify', { method: 'POST', body: JSON.stringify(input) }),
  /** Какие способы регистрации доступны с этого адреса: по почте — только не из России. */
  methods: () => api<AuthMethods>('/auth/methods'),
  /** Регистрация по почте: код письмом вместо SMS. */
  startEmailRegistration: (email: string, captchaToken?: string) =>
    api<PhoneStart>('/auth/register/email', { method: 'POST', body: JSON.stringify({ email, captcha_token: captchaToken }) }),
  /** Вход по номеру или по почте — что ввели, то и шлём. */
  login: (identifier: string, password: string, code?: string) =>
    api<TokenPair>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        ...(identifier.includes('@') ? { email: identifier.trim() } : { phone: identifier }),
        password,
        ...(code ? { code } : {}),
      }),
    }),
  startRecovery: (phone: string, captchaToken?: string) =>
    api<PhoneStart>('/auth/recover/phone', {
      method: 'POST',
      body: JSON.stringify(captchaToken ? { phone, captcha_token: captchaToken } : { phone }),
    }),
  completeRecovery: (input: { verification_id: string; code: string; new_password: string }) =>
    api<TokenPair>('/auth/recover/verify', { method: 'POST', body: JSON.stringify(input) }),
  /**
   * Проверить код на шаге его ввода: ошибка видна сразу, а не после ника или нового пароля.
   * Проверенный код сервер запоминает, и завершение регистрации или восстановления его не тратит.
   */
  checkCode: (id: string, purpose: 'register' | 'recover', code: string) =>
    api<void>(`/auth/verify/${id}/check`, { method: 'POST', body: JSON.stringify({ purpose, code }) }),
  /** Состояние подтверждения номера; попытку не расходует, можно опрашивать. */
  verifyStatus: (id: string, purpose: string) => api<VerifyProgress>(`/auth/verify/${id}?purpose=${purpose}`),
  /** «Не приходит код»: та же проверка продолжается нашим шестизначным кодом по SMS. */
  verifyForceSMS: (id: string, purpose: string) => api<VerifyProgress>(`/auth/verify/${id}/sms?purpose=${purpose}`, { method: 'POST' }),
  logout: () => authed<void>('/auth/logout', { method: 'POST' }),
  me: () => authed<CurrentUser>('/users/@me'),
  /** Профиль: display_name (пусто — сбросить), avatar — data URI или null. */
  /** Порядок серверов слева: сохраняется за учётной записью, а не в браузере. */
  setGuildOrder: (guilds: string[]) =>
    authed<void>('/users/@me/guild-order', { method: 'PATCH', body: JSON.stringify({ guilds }) }),
  changeUsername: (username: string) =>
    authed<CurrentUser>('/users/@me/username', { method: 'PATCH', body: JSON.stringify({ username }) }),
  updateProfile: (input: {
    display_name?: string
    bio?: string
    avatar?: string | null
    banner?: string | null
    /** Украшения: имя из GET /users/@me/decorations, null — снять. */
    avatar_decoration?: string | null
    profile_effect?: string | null
    display_name_styles?: NameStyle | null
    /** Цвета профиля (два цвета) или null; витрина — до трёх достижений. */
    theme_colors?: number[] | null
    profile_showcase?: string[]
    /** Сервер, чей тег носить; null — без тега. Табличка — имя из каталога или null. */
    server_tag?: string | null
    nameplate?: string | null
  }) =>
    authed<CurrentUser>('/users/@me', { method: 'PATCH', body: JSON.stringify(input) }),
  sessions: () => authed<Session[]>('/auth/sessions'),
  revokeOtherSessions: () => authed<void>('/auth/sessions', { method: 'DELETE' }),
  version: () => api<VersionInfo>('/version'),

  mfaStatus: () => authed<MFAStatus>('/auth/mfa'),
  enableTOTP: (input: { password: string; secret: string; code: string }) =>
    authed<{ backup_codes: string[] }>('/auth/mfa/totp', { method: 'POST', body: JSON.stringify(input) }),
  disableTOTP: (code: string) => authed<void>('/auth/mfa/totp', { method: 'DELETE', body: JSON.stringify({ code }) }),
  regenerateBackupCodes: (password: string) =>
    authed<{ backup_codes: string[] }>('/auth/mfa/codes', { method: 'POST', body: JSON.stringify({ password }) }),

  /** Смена пароля: остальные сеансы завершаются, текущий остаётся. */
  changePassword: (input: { password: string; new_password: string }) =>
    authed<void>('/users/@me/password', { method: 'POST', body: JSON.stringify(input) }),

  changePhoneStart: (input: { password: string; phone: string }) =>
    authed<PhoneStart>('/users/@me/phone', { method: 'POST', body: JSON.stringify(input) }),
  changePhoneComplete: (input: { verification_id: string; code: string }) =>
    authed<CurrentUser>('/users/@me/phone', { method: 'PUT', body: JSON.stringify(input) }),

  /** Почта: письмо со ссылкой на новый адрес; привяжется после перехода по ней. */
  emailStart: (input: { password: string; email: string }) =>
    authed<CurrentUser>('/users/@me/email', { method: 'POST', body: JSON.stringify(input) }),
  emailResend: () => authed<CurrentUser>('/users/@me/email/resend', { method: 'POST' }),
  emailRemove: (password: string) => authed<CurrentUser>('/users/@me/email', { method: 'DELETE', body: JSON.stringify({ password }) }),
  /** Удаление учётной записи: заявка с отсрочкой, отменяется до срока. */
  requestDeletion: (password: string) =>
    authed<DeletionStatus>('/users/@me/delete', { method: 'POST', body: JSON.stringify({ password }) }),
  cancelDeletion: () => authed<DeletionStatus>('/users/@me/delete', { method: 'DELETE' }),
  /** Выгрузка своих данных: готовится в фоне, ссылка временная. */
  requestExport: () => authed<DataExport>('/users/@me/export', { method: 'POST' }),
  exportStatus: () => authed<DataExport>('/users/@me/export'),

  /** Ссылка из письма; сессия не нужна — письмо могли открыть на другом устройстве. */
  emailVerify: (token: string) => api<{ email: string }>('/auth/email/verify', { method: 'POST', body: JSON.stringify({ token }) }),
  recoverByEmail: (email: string, captchaToken?: string) =>
    api<{ ok: boolean }>('/auth/recover/email', {
      method: 'POST',
      body: JSON.stringify(captchaToken ? { email, captcha_token: captchaToken } : { email }),
    }),
  recoverByEmailComplete: (input: { token: string; new_password: string }) =>
    api<TokenPair>('/auth/recover/email/verify', { method: 'POST', body: JSON.stringify(input) }),
}

/** «через минуту», «через 7 минут», «через час» — по секундам из ответа. */
function waitFor(seconds?: number): string {
  if (!seconds || seconds <= 0) return 'попозже'
  if (seconds <= 90) return 'через минуту'
  const minutes = Math.ceil(seconds / 60)
  if (minutes < 60) return `через ${minutes} ${plural(minutes, 'минуту', 'минуты', 'минут')}`
  const hours = Math.ceil(minutes / 60)
  return hours === 1 ? 'через час' : `через ${hours} ${plural(hours, 'час', 'часа', 'часов')}`
}

export interface MFAStatus {
  totp_enabled: boolean
  backup_codes_left: number
}

/** Код ошибки «нужен второй фактор» — вход переходит к шагу ввода кода. */
export const CODE_MFA_REQUIRED = 60003
export const CODE_MFA_INVALID = 60008

/** Текст ошибки для формы: по полям из errors, по кодам API, иначе общее сообщение. */
export function describeError(err: unknown): { message: string; fields: Record<string, string> } {
  if (!(err instanceof ApiError)) {
    return { message: 'Нет связи с сервером. Проверьте подключение и попробуйте ещё раз.', fields: {} }
  }
  const fields: Record<string, string> = {}
  const errors = err.errors as Record<string, { _errors?: { message: string }[] }> | undefined
  if (errors && typeof errors === 'object') {
    for (const [field, value] of Object.entries(errors)) {
      const msg = value?._errors?.[0]?.message
      if (msg) fields[field] = msg
    }
  }
  if (err.code === 20016) {
    // медленный режим канала: пауза от секунд до часов
    const s = Math.ceil(err.retryAfter ?? 0)
    return { message: `В канале медленный режим. Отправить снова можно ${s > 0 && s < 90 ? `через ${s} с` : waitFor(err.retryAfter)}.`, fields }
  }
  if (err.code === 20029) return { message: `Имя пользователя можно менять раз в сутки. Попробуйте снова ${waitFor(err.retryAfter)}.`, fields }
  if (err.status === 429) {
    // сервер присылает точное время: лимит бывает и минутный, и часовой,
    // а «подождите минуту» на часовом — обещание, которое мы не сдержим
    return { message: `Слишком много попыток. Попробуйте снова ${waitFor(err.retryAfter)}.`, fields }
  }
  if (err.code === 70003) return { message: err.message, fields: { code: err.message } }
  if (err.code === CODE_MFA_INVALID) return { message: 'Неверный код. Проверьте время на телефоне и попробуйте снова.', fields: { code: 'Неверный код' } }
  if (err.code === CODE_MFA_REQUIRED) return { message: 'Нужен код двухфакторной аутентификации.', fields: { code: 'Введите код' } }
  if (err.code === 50014) return { message: 'Неверный номер или пароль.', fields: {} }
  if (Object.keys(fields).length) return { message: 'Проверьте выделенные поля.', fields }
  return { message: err.message || 'Что-то пошло не так. Попробуйте ещё раз.', fields }
}
